import { useCallback } from 'react';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { bulkUpdateReportingStatus } from '../../../../../services/queries/MaggieCompanyQueries';
import { companyStateJ, getCompanySyncIfResolved } from '../../../../../services/state/CompanyStateJ';
import { getForesightStore } from '../../../../../util/jotai-store';
import { LoadingId } from '../../../../../types';
import { useInvalidateKPIRequests } from '../../KPIRequests/KPIRequestResponse/hooks/useInvalidateKPIRequests';

interface IBulkUpdateReportingStatusError {
  companyId: number;
  name: string;
  message: string;
}
export interface IBulkUpdateReportingStatusResponse {
  processed: ICompanyDataModel[];
  errors: IBulkUpdateReportingStatusError[];
}

export interface IBulkUpdateReportingStatusPayload {
  companyIds: number[];
  value: boolean;
}

export function useSetReportingStatus() {
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();
  const invalidateKpiRequests = useInvalidateKPIRequests();
  const { actions } = useLoadingBarState();

  return useCallback(
    async (companyIds: number[], reporting: boolean) => {
      const store = getForesightStore();
      actions.startLoading(LoadingId.bulkUpdateReportingStatus);
      try {
        const res = await bulkUpdateReportingStatus(companyIds, reporting);

        if (res!.processed && res!.processed.length > 0) {
          for (const { id } of res!.processed) {
            const prev = getCompanySyncIfResolved(id);
            if (prev) {
              store.set(companyStateJ(id), { ...prev, reporting });
            }
          }
          invalidateKpiRequests();
        }

        if (res!.errors && res!.errors.length > 0) {
          const names = res!.errors
            .map((err) => getCompanySyncIfResolved(err.companyId)?.name)
            .filter(Boolean)
            .join(', ');
          pushErrorToast({
            message: `Failed to update reporting status for ${names}`,
          });
        } else {
          pushSuccessToast({
            message: `Data collection is now ${reporting ? 'enabled' : 'disabled'}`,
          });
        }
      } catch (error) {
        console.error(error);
        pushErrorToast({
          message: getErrorMessage(error, 'Failed to update reporting status'),
        });
      } finally {
        actions.stopLoading(LoadingId.bulkUpdateReportingStatus);
      }
    },
    [actions, invalidateKpiRequests, pushErrorToast, pushSuccessToast]
  );
}
